import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AgentDto } from '../../../service/dto';
import { HttpAgentSelfService } from './../../../service/http-agent.service';
import { Component, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
	selector: 'app-agent-self',
	standalone: false,
	templateUrl: './agent-self.component.html',
	styleUrl: './agent-self.component.scss'
})
export class AgentSelfComponent implements OnInit {
	form?: FormGroup;
	agent?: AgentDto;
	loading = false;
	saving = false;
	showNewPassword = false;
	showConfirmPassword = false;

	constructor(
		private httpService: HttpAgentSelfService,
		private fb: FormBuilder,
		private notification: NzNotificationService,
		private message: NzMessageService
	) { }

	ngOnInit(): void {
		this.loading = true;
		this.httpService.getSelf().subscribe({
			next: (agent) => {
				this.agent = agent;
				this.buildForm();
				this.loading = false;
			},
			error: () => {
				this.message.error('Failed to load profile data');
				this.loading = false;
			}
		});
	}

	private buildForm(): void {
		const formOptions: AbstractControlOptions = {
			asyncValidators: [],
			updateOn: 'change',
			validators: group => {
				const newPassword = group.get('newPassword')?.value;
				const confirmPassword = group.get('confirmPassword')?.value;
				if (newPassword && !confirmPassword) {
					return { confirmPasswordRequired: true };
				}
				if (newPassword && confirmPassword && newPassword !== confirmPassword) {
					return { passwordMismatch: true };
				}
				return null;
			}
		};
		this.form = this.fb.group({
			id: [{ value: this.agent!.id, disabled: true }],
			username: [{ value: this.agent!.username, disabled: true }],
			firstName: [this.agent!.firstName, [Validators.required, Validators.maxLength(100)]],
			lastName: [this.agent!.lastName, [Validators.required, Validators.maxLength(100)]],
			emailAddress: [this.agent!.emailAddress, [Validators.required, Validators.email]],
			msisdn: [this.agent!.msisdn, [Validators.pattern(/^\+?[0-9]{10,15}$/)]],
			newPassword: ['', [Validators.minLength(8)]],
			confirmPassword: ['', [Validators.minLength(8)]],
		}, formOptions);

		this.form.get('confirmPassword')?.valueChanges.subscribe(() => {
			this.form!.updateValueAndValidity();
		});

		this.form.get('newPassword')?.valueChanges.subscribe(() => {
			this.form!.updateValueAndValidity();
		});
	}

	get controls() {
		return this.form!.controls;
	}

	onSubmit(): void {
		if (!this.form) {
			return
		};

		Object.values(this.form.controls).forEach(control => {
			control.markAsDirty();
			control.updateValueAndValidity();
		});

		if (this.form.invalid) {
			return
		};

		this.saving = true;

		const payload: AgentDto = {
			id: this.agent!.id,
			createdAt: this.agent!.createdAt,
			username: this.agent!.username,
			firstName: this.form.get('firstName')!.value,
			lastName: this.form.get('lastName')!.value,
			emailAddress: this.form.get('emailAddress')!.value,
			msisdn: this.form.get('msisdn')!.value,
			status: 'Active',
			superUser: true,
			roles: [],
			properties: {},
			hashCode: 0
		};

		if (this.form.get('newPassword')!.value) {
			payload.password = this.form.get('newPassword')!.value;
		}


		this.httpService.updateSelf(payload).subscribe({
			next: (result) => {
				this.message.success('Profile updated successfully');
				this.saving = false;
				if (result.passwordPolicyErrors && result.passwordPolicyErrors.length > 0) {
					console.log('Password policy errors:', result.passwordPolicyErrors);
					const errorMessage = result.passwordPolicyErrors.join('<br>');
					this.notification.error('Password requirements not met', errorMessage, { nzDuration: 10000 });
				} else {
					this.message.success('Profile updated successfully');
					this.agent=result;
					this.buildForm();
				}
			},
			error: (err) => {
				this.message.error(err.error?.message || 'Failed to update profile');
				this.saving = false;
			}
		});
	}
}
